import { defineStore } from "pinia";
import { useAuthStore } from "@/stores/auth";
import global from "@/global";

export const useUserProjectsStore = defineStore("projects", {
  state: () => ({
    projects: JSON.parse(localStorage.getItem("userProjects")) || [],
    currentProjectID: JSON.parse(localStorage.getItem("currentProjectID")) || 0,
    annualProgramsID: JSON.parse(localStorage.getItem("annualProgramsID")) || 0,
    isCollaborationOpen: false,
    QuestionUpdate: 0,
    corePrograms: JSON.parse(localStorage.getItem("corePrograms")) || [],
    isLoading: false
  }),
  actions: {
    getQuestionUpdate(){
      return this.QuestionUpdate;
    },
    setQuestionUpdate(){
      if(this.QuestionUpdate===100){
        this.QuestionUpdate=0;
      }else{
        this.QuestionUpdate++;
      }
    },
    getCollaborationVal(){
      return this.isCollaborationOpen;
    },
    setCollaboration(){
      this.isCollaborationOpen = !this.isCollaborationOpen;
    },
    async getUserProjects() {
      const authStore = useAuthStore();
      if (!authStore.user?.accessToken) {
        console.log('No access token available, skipping user projects fetch');
        return;
      }

      try {
        const res = await fetch(`${global.API_URL}/projects`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authStore.user.accessToken}`,
          },
        });

        const data = await res.json();

        const projectsInOrder = data.projects.sort((a, b) => a.priority - b.priority);

        this.projects = projectsInOrder;
        localStorage.setItem("userProjects", JSON.stringify(projectsInOrder));

        let annualProgramsProject = data.projects.filter((p) => p.project_name == "Annual Programs");
        if (annualProgramsProject.length > 0) {
          localStorage.setItem("annualProgramsID", JSON.stringify(annualProgramsProject[0].id));
          this.annualProgramsID = annualProgramsProject[0].id;
        }

        let temp = this.projects.filter((p) => p.id == this.currentProjectID);

        if (!this.currentProjectID || temp.length == 0) {
          if (annualProgramsProject.length > 0) {
            this.currentProjectID = annualProgramsProject[0].id;
            localStorage.setItem("currentProjectID", JSON.stringify(annualProgramsProject[0].id));
          }
        }

        // Fetch core programs after getting user projects
        await this.fetchCorePrograms();
      } catch (error) {
        console.error('Error fetching user projects:', error);
      }
    },
    updateUserProjects(payload) {
      const projectsInOrder = payload.projects.sort((a, b) => a.priority - b.priority);
      this.projects = projectsInOrder;
      localStorage.setItem("userProjects", JSON.stringify(projectsInOrder));
    },
    removeProject(payload) {
      let updatedProjects = this.projects.filter((p) => p.id != payload.id);
      this.projects = updatedProjects;
      localStorage.setItem("userProjects", JSON.stringify(updatedProjects));
    },
    setCurrentProjectId(payload) {
      this.currentProjectID = parseInt(payload.id);
      localStorage.setItem("currentProjectID", JSON.stringify(parseInt(payload.id)));
    },
    async fetchCorePrograms() {
      const authStore = useAuthStore();
      if (!authStore.user?.accessToken) {
        return;
      }

      try {
        this.isLoading = true;
        const response = await fetch(`${global.API_URL}/core-programs`, {
          headers: {
            Authorization: `Bearer ${authStore.user.accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        
        // Extract corePrograms from the response
        const corePrograms = data?.corePrograms || [];
        
        if (Array.isArray(corePrograms)) {
          this.corePrograms = corePrograms;
          // Update localStorage to keep it synchronized
          localStorage.setItem('corePrograms', JSON.stringify(corePrograms));
          // Add timestamp for staleness checking
          localStorage.setItem('coreProgramsTimestamp', Date.now().toString());
        } else {
          this.corePrograms = [];
          localStorage.setItem('corePrograms', JSON.stringify([]));
          localStorage.setItem('coreProgramsTimestamp', Date.now().toString());
        }
      } catch (error) {
        console.error('Failed to fetch core programs:', error);
        this.corePrograms = [];
        localStorage.setItem('corePrograms', JSON.stringify([]));
      } finally {
        this.isLoading = false;
      }
    },
    setCorePrograms(programs) {
      this.corePrograms = programs;
      // Update localStorage to keep it synchronized
      localStorage.setItem('corePrograms', JSON.stringify(programs));
      // Add timestamp for staleness checking
      localStorage.setItem('coreProgramsTimestamp', Date.now().toString());
    },
    // Sync core programs from localStorage to store
    syncCoreProgramsFromStorage() {
      const storedPrograms = JSON.parse(localStorage.getItem("corePrograms") || "[]");
      this.corePrograms = storedPrograms;
      // Add timestamp if not present
      if (!localStorage.getItem('coreProgramsTimestamp')) {
        localStorage.setItem('coreProgramsTimestamp', Date.now().toString());
      }
      return storedPrograms;
    },
    // Sync new projects from localStorage to store
    syncNewProjectsFromStorage() {
      const storedProjects = JSON.parse(localStorage.getItem("newProjects") || "[]");
      // Add timestamp if not present
      if (!localStorage.getItem('newProjectsTimestamp')) {
        localStorage.setItem('newProjectsTimestamp', Date.now().toString());
      }
      return storedProjects;
    },
    // Get new projects (from localStorage or API if needed)
    async getNewProjects() {
      let newProjects = this.syncNewProjectsFromStorage();
      if (!newProjects.length) {
        newProjects = await this.fetchNewProjects();
      }
      return newProjects;
    },
    // Fetch new projects from API
    async fetchNewProjects() {
      const authStore = useAuthStore();
      if (!authStore.user?.accessToken) {
        return [];
      }

      try {
        const response = await fetch(`${global.API_URL}/projects`, {
          headers: {
            Authorization: `Bearer ${authStore.user.accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        const newProjects = data?.projects || [];
        
        // Update localStorage to keep it synchronized
        localStorage.setItem('newProjects', JSON.stringify(newProjects));
        // Add timestamp for staleness checking
        localStorage.setItem('newProjectsTimestamp', Date.now().toString());
        
        return newProjects;
      } catch (error) {
        console.error('Failed to fetch new projects:', error);
        return [];
      }
    },
    // Initialize store
    async initialize() {
      await this.getUserProjects();
    },
    // Initialize store with all necessary data
    async initializeWithAllData() {
      try {
        // First get user projects (this also fetches core programs)
        await this.getUserProjects();
        
        // Then fetch new projects if not already in localStorage
        const newProjects = this.syncNewProjectsFromStorage();
        if (!newProjects.length) {
          await this.fetchNewProjects();
        }
      } catch (error) {
        console.error('Error initializing projects store with all data:', error);
      }
    },
    // Add a new core program
    async addCoreProgram(program) {
      const authStore = useAuthStore();
      try {
        const response = await fetch(`${global.API_URL}/core-programs`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${authStore.user.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(program),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const newProgram = await response.json();
        this.corePrograms = [...this.corePrograms, newProgram];
        // Update localStorage to keep it synchronized
        localStorage.setItem('corePrograms', JSON.stringify(this.corePrograms));
        // Add timestamp for staleness checking
        localStorage.setItem('coreProgramsTimestamp', Date.now().toString());
        return newProgram;
      } catch (error) {
        console.error('Failed to add core program:', error);
        throw error;
      }
    },
    // Remove a core program
    async removeCoreProgram(programId) {
      const authStore = useAuthStore();
      try {
        const response = await fetch(`${global.API_URL}/core-programs/${programId}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${authStore.user.accessToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        this.corePrograms = this.corePrograms.filter(p => p.id !== programId);
        // Update localStorage to keep it synchronized
        localStorage.setItem('corePrograms', JSON.stringify(this.corePrograms));
        // Add timestamp for staleness checking
        localStorage.setItem('coreProgramsTimestamp', Date.now().toString());
      } catch (error) {
        console.error('Failed to remove core program:', error);
        throw error;
      }
    },
    // Update core programs (for reordering or bulk updates)
    async updateCorePrograms(programs) {
      const authStore = useAuthStore();
      try {
        const response = await fetch(`${global.API_URL}/core-programs`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${authStore.user.accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ corePrograms: programs }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        this.corePrograms = programs;
        // Update localStorage to keep it synchronized
        localStorage.setItem('corePrograms', JSON.stringify(programs));
        // Add timestamp for staleness checking
        localStorage.setItem('coreProgramsTimestamp', Date.now().toString());
      } catch (error) {
        console.error('Failed to update core programs:', error);
        throw error;
      }
    }
  },
});
